import axios from "axios";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";

const colors = {
  red: "#a20000",
  redDark: "#700000",
  redSoft: "#fbebeb",
  ink: "#171717",
  muted: "#666666",
  line: "#e8e8e8",
  surface: "#f7f7f7",
  white: "#ffffff",
};

function useViewportWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

const container = (max = 1200) => ({
  width: "100%",
  maxWidth: max,
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: 20,
  paddingRight: 20,
});

const btnBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 9,
  padding: "13px 20px",
  borderRadius: 999,
  fontWeight: 800,
  border: "1px solid transparent",
  cursor: "pointer",
  textDecoration: "none",
  fontSize: 15,
};

const btnPrimary = {
  ...btnBase,
  background: colors.red,
  color: "#fff",
  boxShadow: "0 10px 24px rgba(162,0,0,.24)",
};

const btnSecondary = {
  ...btnBase,
  borderColor: colors.red,
  color: colors.red,
  background: "#fff",
};

const card = {
  background: "#fff",
  border: `1px solid ${colors.line}`,
  borderRadius: 22,
  padding: 26,
  boxShadow: "0 10px 28px rgba(0,0,0,.045)",
};

const specListItem = {
  padding: "11px 0",
  borderBottom: `1px solid ${colors.line}`,
};

const specStrong = {
  display: "block",
  color: colors.red,
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: 0.3,
};

const kicker = {
  color: colors.red,
  fontSize: 13,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 1,
};

const eyebrow = {
  display: "inline-flex",
  padding: "7px 12px",
  borderRadius: 999,
  background: colors.redSoft,
  color: colors.red,
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: 0.8,
  textTransform: "uppercase",
};

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${colors.line}` }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: "none",
          border: 0,
          padding: "20px 0",
          textAlign: "left",
          font: "inherit",
          fontWeight: 850,
          fontSize: 18,
          display: "flex",
          justifyContent: "space-between",
          cursor: "pointer",
          color: colors.ink,
        }}
      >
        {q}
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 0 20px", color: colors.muted }}>{a}</div>
      )}
    </div>
  );
}

const overviewCards = [
  { icon: "▦", title: "Modular Architecture", text: "Scalable configurations for changing project requirements." },
  { icon: "⚡", title: "High Performance", text: "Designed for commercial and utility energy-management duties." },
  { icon: "◉", title: "Advanced Safety", text: "LFP chemistry, monitoring and multi-layer protection functions." },
  { icon: "⌁", title: "System Integration", text: "Communication options for compatible EMS, HMI and remote monitoring." },
];

const applicationCards = [
  { title: "Peak Shaving", text: "Discharge stored energy during periods of high site demand." },
  { title: "Load Shifting", text: "Move energy use from high-cost periods to lower-cost periods." },
  { title: "Time-of-Use Optimisation", text: "Operate according to applicable tariff windows." },
  { title: "Demand Management", text: "Reduce sudden demand peaks and improve load control." },
  { title: "Backup Power", text: "Support selected loads within a properly engineered backup architecture." },
  { title: "Solar Self-Consumption", text: "Store surplus solar energy for later use." },
];

const processSteps = [
  { title: "Assessment", text: "Review load, tariff, generation and operating objectives." },
  { title: "Sizing", text: "Define power, energy capacity and required duration." },
  { title: "Engineering", text: "Develop architecture, controls and safety design." },
  { title: "Integration", text: "Coordinate supply, installation and site interfaces." },
  { title: "Commissioning", text: "Test functionality and configure monitoring." },
];

const bessFaqs = [
  {
    q: "What is a Battery Energy Storage System?",
    a: "A BESS stores electrical energy in batteries and supplies it later according to the operating strategy and system configuration.",
  },
  {
    q: "What is the difference between the two systems?",
    a: "The 125 kW / 261.2 kWh model is an all-in-one C&I platform. The 5 MWh model is a containerised utility-scale battery system.",
  },
  {
    q: "Can the systems integrate with solar plants?",
    a: "Yes, within a properly engineered solar-plus-storage architecture including PCS, EMS, protection and grid-interface equipment as required.",
  },
];

const ciSpecRows = [
  ["Model", "GSPL-125X261"],
  ["Nominal DC voltage", "832 V"],
  ["Battery voltage range", "702–936 V"],
  ["Battery type", "LFP 314 Ah"],
  ["Total energy", "261.2 kWh"],
  ["Nominal power", "125 kW"],
  ["Maximum power", "137.5 kW"],
  ["Depth of discharge", "90%"],
  ["Dimensions", "1000 × 2185 × 1350 mm"],
  ["Weight", "2350 kg"],
  ["Operating temperature", "−30°C to +55°C"],
  ["Communication", "RS485 / CAN / Ethernet / 4G / Wi-Fi"],
  ["Certification standard listed", "IEC 62619 / UN38.3"],
];

const utilitySpecRows = [
  ["Model", "GS-5000-LC"],
  ["Battery type", "LFP 314 Ah"],
  ["Rated DC capacity", "5,015 kWh"],
  ["Configuration", "12 × 1P416S"],
  ["Rated DC voltage", "1,331.2 V"],
  ["Operating voltage range", "1,164.8–1,497.6 V"],
  ["Maximum charge/discharge ratio", "0.5P / 0.5P"],
  ["Cycle life", "8,000 cycles at 25°C, 80% DoD, 0.5P"],
  ["Dimensions", "6,096 × 2,438 × 2,896 mm"],
  ["Weight", "Approximately 43 tons"],
  ["Enclosure", "20 ft outdoor container, IP55"],
  ["Communication", "CAN / RS485 / Ethernet"],
  ["Monitoring", "Local HMI / Remote Monitoring"],
];

function SpecTable({ rows }) {
  return (
    <div style={{ overflow: "auto", border: `1px solid ${colors.line}`, borderRadius: 18, background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
        <thead>
          <tr>
            <th style={{ padding: "14px 16px", textAlign: "left", background: colors.red, color: "#fff", position: "sticky", top: 0 }}>Parameter</th>
            <th style={{ padding: "14px 16px", textAlign: "left", background: colors.red, color: "#fff", position: "sticky", top: 0 }}>Specification</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row[0]}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: "14px 16px", borderBottom: i === rows.length - 1 ? "none" : `1px solid ${colors.line}`, textAlign: "left" }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BessPage() {
  const width = useViewportWidth();
  const isTablet = width <= 980;
  const isMobile = width <= 640;
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("ci");

  const heroGridCols = isTablet ? "1fr" : "1.05fr .95fr";
  const gridColsN = (n) => (isMobile ? "1fr" : isTablet ? `repeat(${Math.min(n, 2)},1fr)` : `repeat(${n},1fr)`);
  const productGridCols = isTablet ? "1fr" : ".9fr 1.1fr";
  const specGridCols = isMobile ? "1fr" : "repeat(2,1fr)";
  const formGridCols = isMobile ? "1fr" : "repeat(2,1fr)";
  const processGridCols = isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(5,1fr)";

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    companyName: "",
    phone: "",
    remark: "",
    application: "",
    capacity: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      alert('Enter a valid email address')

      return false;
    }
    if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s+/g, ""))) {
      alert('Enter a valid 10-digit mobile number')
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {


      let utm = localStorage.getItem('utm_data') || null;

      let dataVal = {
        ...formData,
        utm
      }

      console.log(dataVal);
      // wire this up to your backend
      const res = await axios.post('https://gautamsolar.us/submit-bess', dataVal);

      if (res?.data?.success) {
        alert('Form submitted successfully!');
        localStorage.removeItem('utm_data');
        setFormData({
          fullName: "",
          email: "",
          companyName: "",
          phone: "",
          remark: "",
          application: "",
          capacity: ""
        })
      }
    }
    catch (er) {
      console.log(er);
    }
    finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div style={{ fontFamily: "Inter, Arial, Helvetica, sans-serif", color: colors.ink, background: "#fff", lineHeight: 1.6 }}>

      <Helmet>
        <title>Battery Energy Storage Systems (BESS) - C&I & Utility Scale</title>
        <meta
          name="description"
          content="Gautam Solar's BESS portfolio includes a 125 kW / 261.2 kWh all-in-one C&I energy storage system and a 5,015 kWh liquid-cooled utility-scale container, for peak shaving, load shifting, backup power, and solar self-consumption."
        />

        <meta
          name="keywords"
          content="battery energy storage system, BESS India, C&I energy storage, utility scale battery storage, LFP battery storage, solar energy storage system"
        />

        <link rel="canonical" href="https://gautamsolar.com/bess/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Gautam Solar Battery Energy Storage System (BESS)",
            image:"https://gautamsolar.com/bess1.jpeg",
            description:
              "Battery Energy Storage System portfolio covering a 125 kW / 261.2 kWh all-in-one C&I platform (GSPL-125X261) and a 5,015 kWh liquid-cooled utility-scale container (GS-5000-LC), both using LFP battery chemistry.",
            brand: { "@type": "Brand", name: "Gautam Solar" },
            manufacturer: {
              "@type": "Organization",
              name: "Gautam Solar",
              url: "https://gautamsolar.com/",
            },
            category: "Battery Energy Storage System",
            
            offers: {
              "@type": "Offer",
              availability: "https://schema.org/InStock",
              priceCurrency: "INR",
              price: "0",
              priceValidUntil: "2027-12-31",
              url: "https://gautamsolar.com/bess/",
              seller: {
                "@type": "Organization",
                name: "Gautam Solar",
              },
            },
            additionalProperty: [
              { "@type": "PropertyValue", name: "C&I Model Total Energy", value: "261.2 kWh" },
              { "@type": "PropertyValue", name: "C&I Model Rated Power", value: "125 kW" },
              { "@type": "PropertyValue", name: "Utility Model Rated DC Capacity", value: "5,015 kWh" },
              { "@type": "PropertyValue", name: "Battery Type", value: "LFP 314 Ah" },
              { "@type": "PropertyValue", name: "Cycle Life (Utility Model)", value: "8,000 cycles at 25°C, 80% DoD, 0.5P" },
            ],
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: bessFaqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
              },
            })),
          })}
        </script>

      </Helmet>

      {/* Hero */}
      <header style={{ position: "relative", overflow: "hidden", background: "linear-gradient(135deg,#fff 0%,#fff 48%,#fff3f4 100%)", padding: isMobile ? "58px 0 48px" : "70px 0 68px" }}>
        <div style={{ ...container(), display: "grid", gridTemplateColumns: heroGridCols, alignItems: "center", gap: 56, position: "relative", zIndex: 1 }}>
          <div>
            <span style={eyebrow}>Battery Energy Storage Systems</span>
            <h1 style={{ fontSize: "clamp(42px,5.5vw,72px)", lineHeight: 1.04, margin: "18px 0", letterSpacing: -2 }}>
              Store Energy. Strengthen Reliability.
            </h1>
            <h2 style={{ fontSize: "clamp(22px,2.4vw,34px)", lineHeight: 1.25, margin: "0 0 16px" }}>
              Smart BESS for C&I and utility-scale applications
            </h2>
            <p style={{ fontSize: 18, color: colors.muted, maxWidth: 720 }}>
              Flexible, scalable energy storage solutions for peak shaving, load shifting, backup power, renewable integration and grid support.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
              <a href="#portfolio" style={btnPrimary}>Explore BESS Range</a>
              <a href="#enquiry" style={btnSecondary}>Discuss Your Project</a>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 26 }}>
              {["LFP Technology", "Liquid Cooling", "Remote Monitoring", "Modular & Scalable"].map((b) => (
                <span key={b} style={{ background: "#fff", border: `1px solid ${colors.line}`, padding: "9px 12px", borderRadius: 12, fontSize: 13, fontWeight: 800, boxShadow: "0 8px 20px rgba(0,0,0,.04)" }}>
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div style={{ background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 30, padding: 18, boxShadow: "0 18px 48px rgba(24,24,24,.10)", transform: isTablet ? "none" : "rotate(1deg)", maxWidth: isTablet ? 620 : "none", margin: isTablet ? "0 auto" : 0 }}>
            <img src="/bess1.jpeg" alt="Gautam Solar utility-scale BESS" style={{ borderRadius: 20, width: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
          </div>
        </div>
      </header>

      {/* Overview */}
      <section id="overview" style={{ padding: isMobile ? "62px 0" : "84px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Energy Storage Portfolio</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Smarter energy management for a changing power system
            </h2>
            <p style={{ color: colors.muted }}>
              The portfolio covers a 125 kW / 261.2 kWh all-in-one C&I system and a 5,015 kWh containerised utility-scale battery system.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: gridColsN(4), gap: 18 }}>
            {overviewCards.map((c) => (
              <article key={c.title} style={card}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: colors.redSoft, color: colors.red, display: "grid", placeItems: "center", fontSize: 22, fontWeight: 900, marginBottom: 18 }}>
                  {c.icon}
                </div>
                <h3 style={{ fontSize: 23, margin: "0 0 12px" }}>{c.title}</h3>
                <p>{c.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section id="portfolio" style={{ padding: isMobile ? "62px 0" : "84px 0", background: colors.surface }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Product Range</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              One portfolio. Multiple energy-storage applications.
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: productGridCols, gap: 30, alignItems: "center", background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 28, padding: isMobile ? 20 : 30, boxShadow: "0 18px 48px rgba(24,24,24,.10)" }}>
            <div style={{ background: "linear-gradient(145deg,#fff,#f4f4f4)", border: `1px solid ${colors.line}`, borderRadius: 22, minHeight: 360, display: "grid", placeItems: "center", padding: 18, overflow: "hidden" }}>
              <img src="/bess125.jpeg" alt="125 kW 261 kWh commercial and industrial BESS" style={{ width: '100%', objectFit: "contain", borderRadius: 14 }} />
            </div>
            <div>
              <span style={eyebrow}>C&I Energy Storage</span>
              <h2 style={{ margin: "8px 0" }}>125 kW / 261.2 kWh</h2>
              <h3 style={{ fontSize: 23, margin: "0 0 12px" }}>All-in-One Smart Energy Storage System</h3>
              <p style={{ color: colors.muted }}>
                Integrated battery storage and power-conversion platform for peak shaving, load shifting, demand management, backup power and solar self-consumption.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: specGridCols, gap: "10px 18px", margin: "20px 0" }}>
                <div style={specListItem}><strong style={specStrong}>Total Energy</strong>261.2 kWh</div>
                <div style={specListItem}><strong style={specStrong}>Rated Power</strong>125 kW</div>
                <div style={specListItem}><strong style={specStrong}>Depth of Discharge</strong>90%</div>
                <div style={specListItem}><strong style={specStrong}>System Efficiency</strong>90%</div>
                <div style={specListItem}><strong style={specStrong}>Enclosure</strong>IP55</div>
                <div style={specListItem}><strong style={specStrong}>Cooling</strong>Battery liquid cooling</div>
              </div>
              <button onClick={() => setActiveTab("ci")} style={{ ...btnPrimary, border: "none" }}>
                <a href="#specs" style={{ color: "#fff", textDecoration: "none" }}>View Specifications</a>
              </button>
            </div>
          </div>

          <br />

          <div style={{ display: "grid", gridTemplateColumns: productGridCols, gap: 30, alignItems: "center", background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 28, padding: isMobile ? 20 : 30, boxShadow: "0 18px 48px rgba(24,24,24,.10)" }}>
            <div style={{ order: isTablet ? 2 : 1 }}>
              <span style={eyebrow}>Utility-Scale Energy Storage</span>
              <h2 style={{ margin: "8px 0" }}>5 MWh BESS</h2>
              <h3 style={{ fontSize: 23, margin: "0 0 12px" }}>GS-5000-LC Liquid-Cooled Container</h3>
              <p style={{ color: colors.muted }}>
                Containerised LFP storage system for large-scale solar, utility and industrial energy-storage projects.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: specGridCols, gap: "10px 18px", margin: "20px 0" }}>
                <div style={specListItem}><strong style={specStrong}>Rated DC Capacity</strong>5,015 kWh</div>
                <div style={specListItem}><strong style={specStrong}>Rated DC Voltage</strong>1,331.2 V</div>
                <div style={specListItem}><strong style={specStrong}>Charge / Discharge Rate</strong>0.5P / 0.5P</div>
                <div style={specListItem}><strong style={specStrong}>Cycle Rating</strong>8,000 cycles*</div>
                <div style={specListItem}><strong style={specStrong}>Enclosure</strong>IP55</div>
                <div style={specListItem}><strong style={specStrong}>Thermal Management</strong>Liquid cooling</div>
              </div>
              <button onClick={() => setActiveTab("utility")} style={{ ...btnPrimary, border: "none" }}>
                <a href="#specs" style={{ color: "#fff", textDecoration: "none" }}>View Specifications</a>
              </button>
            </div>
            <div style={{ order: isTablet ? 1 : 2, background: "linear-gradient(145deg,#fff,#f4f4f4)", border: `1px solid ${colors.line}`, borderRadius: 22, minHeight: 360, display: "grid", placeItems: "center", padding: 18, overflow: "hidden" }}>
              <img src="/bess5kw.jpeg" alt="5 MWh utility-scale BESS" style={{ width: '100%', objectFit: "contain", borderRadius: 14 }} />
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section id="applications" style={{ padding: isMobile ? "62px 0" : "84px 0", background: colors.redSoft }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Operating Modes</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Flexible control for different energy strategies
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: gridColsN(3), gap: 22 }}>
            {applicationCards.map((c) => (
              <div key={c.title} style={card}>
                <h3 style={{ fontSize: 23, margin: "0 0 12px" }}>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specs / Tabs */}
      <section id="specs" style={{ padding: isMobile ? "62px 0" : "84px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Technical Specifications</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              Explore product data
            </h2>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
            <button
              onClick={() => setActiveTab("ci")}
              style={{
                border: `1px solid ${activeTab === "ci" ? colors.red : colors.line}`,
                background: activeTab === "ci" ? colors.red : "#fff",
                color: activeTab === "ci" ? "#fff" : colors.ink,
                padding: "11px 16px",
                borderRadius: 999,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              125 kW / 261.2 kWh
            </button>
            <button
              onClick={() => setActiveTab("utility")}
              style={{
                border: `1px solid ${activeTab === "utility" ? colors.red : colors.line}`,
                background: activeTab === "utility" ? colors.red : "#fff",
                color: activeTab === "utility" ? "#fff" : colors.ink,
                padding: "11px 16px",
                borderRadius: 999,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              5 MWh BESS
            </button>
          </div>
          {activeTab === "ci" ? <SpecTable rows={ciSpecRows} /> : <SpecTable rows={utilitySpecRows} />}
        </div>
      </section>

      {/* Process */}
      <section style={{ padding: isMobile ? "62px 0" : "84px 0", background: colors.surface }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Project Delivery</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              From requirement to commissioning
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: processGridCols, gap: 16 }}>
            {processSteps.map((s, i) => (
              <div key={s.title} style={{ position: "relative", background: "#fff", border: `1px solid ${colors.line}`, borderRadius: 18, padding: "24px 18px" }}>
                <div style={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: "50%", background: colors.red, color: "#fff", fontWeight: 900, marginBottom: 14 }}>
                  {i + 1}
                </div>
                <h3 style={{ fontSize: 23, margin: "0 0 12px" }}>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" style={{ padding: isMobile ? "62px 0" : "84px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 780, marginBottom: 34 }}>
            <span style={kicker}>Frequently Asked Questions</span>
            <h2 style={{ fontSize: "clamp(31px,4vw,48px)", lineHeight: 1.12, margin: "8px 0 14px", letterSpacing: -1 }}>
              BESS FAQs
            </h2>
          </div>
          <div style={{ borderTop: `1px solid ${colors.line}` }}>
            {bessFaqs.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section id="enquiry" style={{ padding: isMobile ? "62px 0" : "70px 0" }}>
        <div style={container()}>
          <div style={{ background: `linear-gradient(135deg,${colors.red},${colors.redDark})`, color: "#fff", borderRadius: 30, padding: isMobile ? 28 : 42, display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1.2fr .8fr", gap: 28, alignItems: "center" }}>
            <div>
              <span style={eyebrow}>Project Consultation</span>
              <h2 style={{ margin: "8px 0 14px", color: "#fff" }}>Discuss your energy storage requirement</h2>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

            <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: formGridCols, gap: 14 }}>
              <input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Full name"
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <input
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Company name"
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address"
                  style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: `1px solid #ddd`, font: "inherit" }}
                />

              </div>
              <div>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Mobile number"
                  style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: `1px solid #ddd`, font: "inherit" }}
                />

              </div>
              <select
                name="application"
                value={formData.application}
                onChange={handleChange}
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              >
                <option value="">Select application</option>
                <option value="ci-storage">C&I Energy Storage</option>
                <option value="solar-plant">Solar Power Plant</option>
                <option value="utility">Utility Project</option>
                <option value="backup">Backup Power</option>
              </select>
              <input
                name="capacity"
                value={formData.capacity}
                onChange={handleChange}
                placeholder="Required capacity"
                style={{ width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <textarea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                placeholder="Project location, voltage, solar capacity and operating objective"
                style={{ gridColumn: "1/-1", minHeight: 120, resize: "vertical", width: "100%", padding: "14px 15px", borderRadius: 12, border: "1px solid #ddd", font: "inherit" }}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  ...btnSecondary,
                  gridColumn: "1/-1",
                  background: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  opacity: isSubmitting ? 0.7 : 1,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                }}
              >
                {isSubmitting && (
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      border: "2px solid rgba(162,0,0,0.25)",
                      borderTopColor: colors.red,
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.7s linear infinite",
                    }}
                  />
                )}
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div >
  );
}
